import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { FolderTree, Pencil, Plus, Trash2 } from "lucide-react";
import { tokens } from "../../theme";
import { AddOnCategoryModel } from "../../utils/models/AddOnCategoryModel";
import { EntitiesListResponseModel } from "../../utils/models/EntitiesListResponseModel";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import {
  createAddOnCategory,
  deleteAddOnCategory,
  getAllAddOnCategories,
  updateAddOnCategory,
} from "./AddOnCategoriesService";

const AddOnCategoriesManager = ({ setAddOnsState }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const setIsLoading = useLoadingSpinner();
  const [categoriesData, setCategoriesData] = useState(EntitiesListResponseModel);
  const [refreshState, setRefreshState] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [formData, setFormData] = useState(AddOnCategoryModel);

  useEffect(() => {
    setIsLoading(true);
    getAllAddOnCategories(
      setCategoriesData,
      { pageSize: "All", page: 0, sortField: "sortOrder", sortOrder: "ASC" },
      setIsLoading,
    ).catch(() => {
      // Error toast handled in service.
    });
  }, [refreshState, setIsLoading]);

  const categories = useMemo(() => categoriesData.content ?? [], [categoriesData.content]);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId],
  );

  useEffect(() => {
    if (selectedCategory) {
      setFormData({ ...selectedCategory });
      return;
    }

    setFormData(AddOnCategoryModel);
  }, [selectedCategory]);

  const resetForm = () => {
    setSelectedCategoryId(null);
    setFormData(AddOnCategoryModel);
  };

  const handleChange = (field) => (event) => {
    const nextValue = field === "active"
      ? event.target.checked
      : field === "sortOrder"
        ? Number(event.target.value || 0)
        : event.target.value;

    setFormData((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      if (formData.id) {
        await updateAddOnCategory(formData, setRefreshState, undefined, setIsLoading);
      } else {
        await createAddOnCategory(formData, setRefreshState, undefined, setIsLoading);
      }
      setAddOnsState?.((prev) => !prev);
      resetForm();
    } catch {
      // Error toast handled in service.
    }
  };

  const handleDelete = async () => {
    if (!formData.id) {
      return;
    }

    setIsLoading(true);

    try {
      await deleteAddOnCategory(formData.id, setRefreshState, undefined, setIsLoading);
      setAddOnsState?.((prev) => !prev);
      resetForm();
    } catch {
      // Error toast handled in service.
    }
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 2, md: 4 },
        borderRadius: "24px",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="800" sx={{ color: colors.grey[100], display: "flex", alignItems: "center", gap: 1 }}>
          <FolderTree size={22} /> Add-On Categories
        </Typography>
        <Typography variant="body2" sx={{ color: colors.grey[300], mt: 1 }}>
          Create and maintain the categories used across add-on management and customer-facing filters.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: "18px", bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[50] }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: colors.grey[100] }}>
              {formData.id ? "Update category" : "Create category"}
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  label="Category name"
                  value={formData.name}
                  onChange={handleChange("name")}
                  required
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={handleChange("description")}
                  multiline
                  minRows={3}
                  fullWidth
                />
                <TextField
                  label="Sort order"
                  type="number"
                  value={formData.sortOrder}
                  onChange={handleChange("sortOrder")}
                  fullWidth
                />
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={Boolean(formData.active)} onChange={handleChange("active")} />
                  <Typography color={colors.grey[100]}>Active</Typography>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button type="submit" variant="contained" startIcon={formData.id ? <Pencil size={16} /> : <Plus size={16} />}>
                    {formData.id ? "Save changes" : "Create category"}
                  </Button>
                  <Button variant="outlined" onClick={resetForm}>Reset</Button>
                  {formData.id && (
                    <Button color="error" variant="outlined" startIcon={<Trash2 size={16} />} onClick={handleDelete}>
                      Delete
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: "18px", bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[50] }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: colors.grey[100] }}>
              Existing categories
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List sx={{ maxHeight: 420, overflowY: "auto" }}>
              {categories.map((category) => (
                <ListItem key={category.id} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={category.id === selectedCategoryId}
                    onClick={() => setSelectedCategoryId(category.id)}
                    sx={{ borderRadius: "14px" }}
                  >
                    <ListItemText
                      primary={category.name}
                      secondary={`Sort: ${category.sortOrder ?? 0} • ${category.active ? "Active" : "Inactive"}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {categories.length === 0 && (
                <Typography variant="body2" color={colors.grey[300]}>
                  No categories have been created yet.
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

AddOnCategoriesManager.propTypes = {
  setAddOnsState: PropTypes.func,
};

export default AddOnCategoriesManager;
